import { StatsTrackerInterface } from '../../stats/tracker.interface';
import { Snowplow2StatsDataProperty } from './data/property';
import { Snowplow2StatsDataAgent } from './data/agent';
import { Snowplow2StatsDataBroker } from './data/broker';
import { Snowplow2StatsDataTrackable } from './data/trackable';
import { Snowplow2StatsDataSearch } from './data/search';
import { StatsDebuggerServiceInterface } from '../../../service/stats-debugger/service.interface';
import { StatsEmitterEvent } from '../../stats/emitter.event';
import { StatsPlatformEnum } from '../../stats/platform.enum';
import { StatsGuardServiceInterface } from '../../../service/stats-guard/service.interface';
import { Snowplow2WindowInterface } from '../window.interface';
import { Snowplow2StatsSchemaContextInterface } from './schema-context.interface';

export class Snowplow2StatsTracker implements StatsTrackerInterface {
  /**
   *
   * @param snowplow2Window
   * @param statsDebuggerService
   * @param statsGuardService
   */
  constructor(
    private snowplow2Window: Snowplow2WindowInterface,
    private statsDebuggerService: StatsDebuggerServiceInterface,
    private statsGuardService: StatsGuardServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public pageView(userId: string, data?: Snowplow2StatsDataTrackable): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.sp2, StatsEmitterEvent.pageView, { userId });

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    let context: Snowplow2StatsSchemaContextInterface[];
    if (data) {
      context = data.getContextSchemaList();
    }

    // Send data
    this.snowplow2Window.snowplow('trackPageView', { context });
    this.snowplow2Window.snowplow('setUserId', userId);
  }

  /**
   * @inheritDoc
   */
  public homePage(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public propertyPage(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyPage);
  }

  /**
   * @inheritDoc
   */
  public propertySerp(data: Snowplow2StatsDataSearch): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertySerp);
  }

  /**
   * @inheritDoc
   */
  public propertyImpression(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyImpression);
  }

  /**
   * @inheritDoc
   */
  public propertyLoad(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyLoad);
  }

  /**
   * @inheritDoc
   */
  public propertyLeadClick(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyLeadClick);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public propertyLeadSend(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyLeadSend);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public propertyClick(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyClick);
  }

  /**
   * @inheritDoc
   */
  public propertySave(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertySave);
  }

  /**
   * @inheritDoc
   */
  public propertyUnsave(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyUnsave);
  }

  /**
   * @inheritDoc
   */
  public propertyImageBrowse(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public agentHome(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public agentPage(data: Snowplow2StatsDataAgent): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentPage);
  }

  /**
   * @inheritDoc
   */
  public agentSerp(data: Snowplow2StatsDataSearch): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentSerp);
  }

  /**
   * @inheritDoc
   */
  public agentImpression(data: Snowplow2StatsDataAgent): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentImpression);
  }

  /**
   * @inheritDoc
   */
  public agentLoad(data: Snowplow2StatsDataAgent): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentLoad);
  }

  /**
   * @inheritDoc
   */
  public agentLeadClick(data: Snowplow2StatsDataAgent): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentLeadClick);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public agentLeadSend(data: Snowplow2StatsDataAgent): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentLeadSend);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public agentClick(data: Snowplow2StatsDataAgent): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.agentClick);
  }

  /**
   * @inheritDoc
   */
  public brokerHome(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public brokerPage(data: Snowplow2StatsDataBroker): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerPage);
  }

  /**
   * @inheritDoc
   */
  public brokerSerp(data: Snowplow2StatsDataSearch): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerSerp);
  }

  /**
   * @inheritDoc
   */
  public brokerImpression(data: Snowplow2StatsDataBroker): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerImpression);
  }

  /**
   * @inheritDoc
   */
  public brokerLoad(data: Snowplow2StatsDataBroker): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerLoad);
  }

  /**
   * @inheritDoc
   */
  public brokerLeadClick(data: Snowplow2StatsDataBroker): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerLeadClick);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public brokerLeadSend(data: Snowplow2StatsDataBroker): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerLeadSend);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public brokerClick(data: Snowplow2StatsDataBroker): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.brokerClick);
  }

  /**
   * @inheritDoc
   */
  public userPage(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public userSignIn(userId: string): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.sp2, StatsEmitterEvent.userSignIn, { userId });

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    this.snowplow2Window.snowplow('setUserId', userId);
  }

  /**
   * @inheritDoc
   */
  public userSignOut(userId: string): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.sp2, StatsEmitterEvent.userSignOut, { userId });

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    this.snowplow2Window.snowplow('setUserId', userId);
  }

  /**
   * @inheritDoc
   */
  public userRegister(userId: string): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.sp2, StatsEmitterEvent.userRegister, { userId });

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    this.snowplow2Window.snowplow('setUserId', userId);
  }

  /**
   * @inheritDoc
   */
  public managerClick(category: string, action: string): void {
    this.collectStructEvent(category, action, StatsEmitterEvent.managerClick);
  }

  /**
   * @inheritDoc
   */
  public ctaClick(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public errorPage(data: any): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public myAccountPage(data: Snowplow2StatsDataTrackable): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.myAccountPage);
  }

  /**
   * @inheritDoc
   */
  public buyVsRentCalculatorPage(): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public propertyReadmoreClick(data: Snowplow2StatsDataTrackable): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyReadmoreClick);
    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * @inheritDoc
   */
  public propertyLeadClickWhatsapp(data: Snowplow2StatsDataProperty): void {
    // Not tracking anything on snowplow2
  }

  /**
   * @inheritDoc
   */
  public propertyLeadSendWhatsapp(data: Snowplow2StatsDataProperty): void {
    this.collectSelfDescribingEvent(data, StatsEmitterEvent.propertyLeadSendWhatsapp);

    this.snowplow2Window.snowplow('flushBuffer');
  }

  /**
   * Collects a SelfDescribingEvent event
   *
   * @param data
   * @param eventName
   */
  private collectSelfDescribingEvent(data: Snowplow2StatsDataTrackable, eventName: StatsEmitterEvent): void {
    const eventSchema = data.getEventSchema();
    const contextSchemaList = data.getContextSchemaList();

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.sp2, eventName, {
      event: 'trackSelfDescribingEvent',
      eventSchema,
      contextSchemaList,
    });

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    // Send data
    this.snowplow2Window.snowplow('trackSelfDescribingEvent', { event: eventSchema, context: contextSchemaList });
  }

  /**
   * Collects a StructEvent event
   *
   * @param category
   * @param action
   * @param eventName
   */
  private collectStructEvent(category: string, action: string, eventName: StatsEmitterEvent): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.sp2, eventName, {
      event: 'trackStructEvent',
      category,
      action,
    });

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    // Send data
    this.snowplow2Window.snowplow('trackStructEvent', { category, action });
  }
}
